import moment from 'moment';
import { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import en from '../../../lang/en-us';
import { CustomRadio } from '../../../lib/CustomRadio';
import { TableColumn, TableWithCollapse } from '../../../lib/DataTable';
import { useTableWithCollapse } from '../../../lib/DataTable/customHooks';
import DatePickerInput from '../../../lib/DatePicker';
import { fileDownload } from '../../../lib/FileDownload';
import { CAS_HISTORY_DATA, CGS_HISTORY_DATA } from '../../../redux-store/types/nct';
import { IsAllow, LIST_PROTFOLIO_PAYLOAD } from '../../../redux-store/types/portfolio';
import { getDateString } from '../../../utils/constants';
import { formatAsIndianCurrency } from '../../../utils/currency';
import { maskPan } from '../../../utils/utilityFunctions';
import SegmentedControl from '../../common/segmented-control';
import NoFoliosFound from '../makeNct/NoFoliosFound';

const { dashboard, sebi } = en;

const SEGMENTS = ['Detailed ', 'My Downloads'];
const SEGMENTSSPAN = ['(Includes transaction listing)'];

const CURRENT_FINANCIAL_YEAR = 'CURRENT_FINANCIAL_YEAR';
const PREVIOUS_FINANCIAL_YEAR = 'PREVIOUS_FINANCIAL_YEAR';
const SPECIFIC_FINANCIAL_YEAR = 'SPECIFIC_FINANCIAL_YEAR';
const LAST_LAST_FINANCIAL_YEAR = 'LAST_LAST_FINANCIAL_YEAR';

const columns: TableColumn[] = [
    {
        headerName: 'Nature of Holding',
        field: 'name',
    },
    {
        headerName: 'Cost Value(Rs.)',
        field: 'costValue',
        valueGetter: ({ value }) => formatAsIndianCurrency(Number(value)) || 'N/A',
    },
    {
        headerName: 'Market Value(Rs.)',
        field: 'currentMktValue',
        valueGetter: ({ value }) => formatAsIndianCurrency(Number(value)) || 'N/A',
    },
];

const columns1: TableColumn[] = [
    {
        headerName: 'Scheme Name',
        field: 'schemeName',
    },
    {
        headerName: 'Invested Value(Rs.)',
        field: 'costValue',
        valueGetter: ({ value }) => formatAsIndianCurrency(Number(value)) || 'N/A',
    },
    {
        headerName: 'Market Value(Rs.)',
        field: 'currentMktValue',
        valueGetter: ({ value }) => formatAsIndianCurrency(Number(value)) || 'N/A',
    },
    {
        headerName: 'Gain/Loss(Absolute)(Rs.)',
        field: 'gainLoss',
        valueGetter: ({ row, value }) =>
            `${formatAsIndianCurrency(Number(value)) ?? 0}(${row.gainLossPercentage || 0}%)`,
    },
    {
        headerName: 'Balance Units',
        field: 'closingBalance',
    },
    {
        headerName: 'Nav Date',
        field: 'navDate',
        valueGetter: ({ row }) => getDateString(row.navDate),
    },
];

type CAS_DETAIL_PDF = {
    pan: string;
    mobile: string;
    pekrn: string;
    reqId: string;
    fromDate: string;
    toDate: string;
    fileType?: string;
};

type CAS_SINGLE_DETAIL_PDF = {
    pan: string;
    mobile: string;
    pekrn: string;
    reqId: string;
    documentId: string;
};

type CGS_SINGLE_DETAIL_PDF = {
    pan: string;
    mobile: string;
    pekrn: string;
    reqId: string;
    documentId: string;
};
interface SebiCgsProps extends RouteComponentProps {
    pan: string;
    mobile: string;
    pekrn: string;
    reqId: string;
    portfolios: any;
    getPortfolio: Function;
    getCgsDocument: Function;
    getCgsHistory: Function;
    getCgsDetail: Function;
    casDetail?: 'Y' | 'N';
    newToMF: IsAllow;
}

const CustomDownloadButton = ({ row, downloadCgsDetailedPDF }: any) => {
    const isDocumentReadyToDownload = row.requestStatus === 'Completed';
    const downloadStatus = () => (row.isDownloading ? () => {} : downloadCgsDetailedPDF(row));
    const color = isDocumentReadyToDownload ? '#33dbb3' : '#2D9CDB';
    return (
        <div
            className="sebi-download"
            role="button"
            style={isDocumentReadyToDownload ? { cursor: 'pointer', color } : { color }}
            onClick={isDocumentReadyToDownload ? () => downloadStatus() : () => {}}
        >
            <img
                src={`/images/${
                    isDocumentReadyToDownload ? 'download-icon.svg' : 'inprogress_icon.svg'
                }`}
                alt=""
            />
            {isDocumentReadyToDownload
                ? row.isDownloading
                    ? 'Downloading'
                    : 'Download'
                : 'In Progress'}
        </div>
    );
};

const ComingSoon = (): JSX.Element => {
    return (
        <div
            className={`folios-not-found `}
            style={{
                marginTop: 20,
            }}
        >
            <img
                className="nct-coming-soon"
                src="/images/coming-soon.svg"
                alt=""
                style={{
                    width: 270,
                    height: 250,
                }}
            />
        </div>
    );
};

const SebiCgs = (props: SebiCgsProps) => {
    const [statementType, setType] = useState(SEGMENTS[0]);
    const [period, setPeriod] = useState<string>(CURRENT_FINANCIAL_YEAR);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [selectedDate, setSelectedDate] =
        useState<{
            from: Date | string | undefined;
            to: Date | string | undefined;
        } | null>(null);

    const [isDownloadingSummaryPDF, setIsDownloadingSummaryPDF] = useState<boolean>(false);

    const [isGeneratingDetailedPDF, setIsGeneratingDetailedPDF] = useState<boolean>(false);
    const [isGeneratingDetailedExcel, setIsGeneratingDetailedExcel] = useState<boolean>(false);

    const [isDownloadingDetailedPDF, setIsDownloadingDetailedPDF] = useState<boolean>(false);

    const [isCgsHistoryDataLoading, setIsCgsHistoryDataLoading] = useState<boolean>(false);
    const [cgsHistoryData, setCgsHistoryData] = useState<CGS_HISTORY_DATA[]>([]);

    const downloadColumns: TableColumn[] = [
        {
            headerName: 'S.NO',
            field: 'id',
            valueGetter: ({ index = 0 }) =>
                (index + 1).toLocaleString('en-US', {
                    minimumIntegerDigits: 2,
                    useGrouping: false,
                }),
        },
        {
            headerName: 'Requested Date & Time',
            field: 'requestDateTime',
            valueGetter: ({ row }) => moment(row.requestDateTime).format('DD-MM-YYYY hh:mm A'),
        },
        {
            headerName: 'From Date',
            field: 'fromDate',
            valueGetter: ({ row }) =>
                row.fromDate !== '' ? moment(row.fromDate).format('DD-MM-YYYY') : 'N/A',
        },
        {
            headerName: 'To Date',
            field: 'toDate',
            valueGetter: ({ row }) =>
                row.toDate !== '' ? moment(row.toDate).format('DD-MM-YYYY') : 'N/A',
        },
        {
            headerName: 'Action',
            field: 'requestStatusPdf',
            renderCell: ({ row }) => (
                <CustomDownloadButton
                    row={row}
                    isDownloadingDetailedPDF={isDownloadingDetailedPDF}
                    downloadCgsDetailedPDF={downloadCgsDetail}
                />
            ),
        },
    ];

    const { tableRows, tableColumns, ...rest } = useTableWithCollapse<CAS_HISTORY_DATA[]>({
        rows: cgsHistoryData,
        columns: downloadColumns,
        loading: isCgsHistoryDataLoading,
    });

    const handleSegmentChange = (selectedSegmentIndex: number) => {
        setType(SEGMENTS[selectedSegmentIndex]);
    };

    useEffect(() => {
        const fetchPortfolio = async () => {
            const { pan = '', pekrn = '', mobile = '', reqId = '' } = props;
            const payload: LIST_PROTFOLIO_PAYLOAD = {
                pan,
                mobile,
                pekrn,
                reqId,
            };
            try {
                await props.getPortfolio(payload);
            } catch (error: any) {
                alert(error.message);
            }
        };
        fetchPortfolio();
    }, []);

    const downloadCgsDetail = async (clickedRow: any) => {
        const clickedDoc = cgsHistoryData.filter(
            (row) => row.documentId == clickedRow.documentId,
        )[0];
        clickedDoc.isDownloading = true;
        setCgsHistoryData([...cgsHistoryData]);
        try {
            const { pan = '', pekrn = '', mobile = '', reqId = '' } = props;
            const payload: CGS_SINGLE_DETAIL_PDF = {
                pan,
                pekrn,
                mobile,
                reqId,
                documentId: clickedRow.documentId,
            };
            const response = await props.getCgsDocument(payload);
            // filename:`cas_detailed_report_${moment().format('YYYY_MM_DD_HHmmss')}.pdf`
            const { errors = [], ...rest } = response.body || {};
            const [firstError] = errors;
            if (Number(firstError?.code) === 1004) {
                throw new Error(firstError?.message);
            }
            return fileDownload({
                data: response.body,
                mime: 'application/vnd.ms-excel',
                filename: `cgs_detailed_report_${moment().format('YYYY_MM_DD_HHmmss')}.${
                    clickedRow.fileType
                }`,
            });
        } catch (error: any) {
            alert(error.message);
        } finally {
            clickedDoc.isDownloading = false;
            setCgsHistoryData([...cgsHistoryData]);
        }
    };

    useEffect(() => {
        if (statementType === SEGMENTS[1]) {
            const fetchCasHistory = async () => {
                setIsCgsHistoryDataLoading(true);
                try {
                    const { pan = '', pekrn = '', mobile = '', reqId = '' } = props;
                    const payload: LIST_PROTFOLIO_PAYLOAD = {
                        pan,
                        mobile,
                        pekrn,
                        reqId,
                    };
                    const { body: casHistoryData = {} } = await props.getCgsHistory(payload);
                    const { history: casHistory = [] } = casHistoryData || {};
                    setCgsHistoryData(
                        casHistory.map((obj: {}) => ({ ...obj, isDownloading: false })).reverse(),
                    );
                } catch (error: any) {
                    alert(error);
                } finally {
                    setIsCgsHistoryDataLoading(false);
                }
            };
            fetchCasHistory();
        }
    }, [statementType]);

    useEffect(() => {
        let fromDate = new Date();
        let toDate = new Date();
        switch (period) {
            case CURRENT_FINANCIAL_YEAR: {
                const currentMonth = new Date().getMonth();
                const currentYear = new Date().getFullYear();
                if (currentMonth > 2) {
                    fromDate = new Date(`04/01/${currentYear}`);
                } else {
                    fromDate = new Date(`04/01/${currentYear - 1}`);
                }
                break;
            }
            case PREVIOUS_FINANCIAL_YEAR: {
                const currentMonth = new Date().getMonth();
                const currentYear = new Date().getFullYear();
                if (currentMonth > 2) {
                    fromDate = new Date(`04/01/${currentYear - 1}`);
                    toDate = new Date(`03/31/${currentYear}`);
                } else {
                    fromDate = new Date(`04/01/${currentYear - 2}`);
                    toDate = new Date(`03/31/${currentYear - 1}`);
                }
                break;
            }
            case LAST_LAST_FINANCIAL_YEAR: {
                const currentMonth = new Date().getMonth();
                const currentYear = new Date().getFullYear();
                if (currentMonth > 2) {
                    fromDate = new Date(`04/01/${currentYear - 2}`);
                    toDate = new Date(`03/31/${currentYear - 1}`);
                } else {
                    fromDate = new Date(`04/01/${currentYear - 3}`);
                    toDate = new Date(`03/31/${currentYear - 2}`);
                }
                break;
            }
            default: {
            }
        }
        setSelectedDate({ from: fromDate, to: toDate });
    }, [period]);

    const toggleDialog = () => {
        setShowDialog((show) => !show);
        setPeriod(CURRENT_FINANCIAL_YEAR);
        handleSegmentChange(1);
    };

    const generateCgsDetail = async (fileType: 'pdf' | 'xlsx') => {
        try {
            if (fileType === 'pdf') {
                setIsGeneratingDetailedPDF(true);
            } else {
                setIsGeneratingDetailedExcel(true);
            }
            const { pan = '', pekrn = '', mobile = '', reqId = '' } = props;
            const fromDate = moment(selectedDate?.from).format('YYYY-MM-DD');
            const toDate = moment(selectedDate?.to).format('YYYY-MM-DD');
            if (fromDate === toDate) {
                throw Error('From date and To date should not be same');
            }
            const payload: CAS_DETAIL_PDF = {
                pan,
                mobile,
                pekrn,
                reqId,
                fromDate: moment(fromDate).format('DD-MMM-YYYY'),
                toDate: moment(toDate).format('DD-MMM-YYYY'),
                fileType,
            };
            const { error } = await props.getCgsDetail(payload);
            if (error === null) toggleDialog();
        } catch (error: any) {
            alert(error.message);
        } finally {
            if (fileType === 'pdf') {
                setIsGeneratingDetailedPDF(false);
            } else {
                setIsGeneratingDetailedExcel(false);
            }
        }
    };

    const handleFromDateChange = (date: Date) => {
        setSelectedDate((prev) => ({
            from: date,
            to: prev?.to,
        }));
    };

    const handleToDateChange = (date: Date) => {
        setSelectedDate((prev) => ({
            from: prev?.from,
            to: date,
        }));
    };
    return (
        <div className="curved-bg-content curved-bg-table-outer sebi-cas">
            <div className="sebi-content-bottom">
                <SegmentedControl
                    additionalClasses="portfolio-details-tabs portfolio-details-main-tab"
                    segments={SEGMENTS}
                    segmentsSpan={SEGMENTSSPAN}
                    onSegmentChanged={handleSegmentChange}
                    selectedSegmentIndex={SEGMENTS.findIndex(
                        (str: string) => str === statementType,
                    )}
                    key={statementType}
                />
            </div>
            {statementType === SEGMENTS[0] ? (
                <>
                    <>
                        <div className="sebi-content-bottom detailed-sebi-period">
                            <div className="sebi-content-right">
                                <div className="sebi-content-heading sebi-content-folioListing">
                                    <p>{sebi.selectPeriod}</p>
                                </div>
                                <CustomRadio
                                    title={''}
                                    name="select-period"
                                    list={[
                                        {
                                            label: 'Current financial year',
                                            value: CURRENT_FINANCIAL_YEAR,
                                            cssClass: 'sebi-radio-button-style',
                                        },
                                        {
                                            label: 'Previous financial year',
                                            value: PREVIOUS_FINANCIAL_YEAR,
                                            cssClass: 'sebi-radio-button-style',
                                        },
                                        {
                                            label: 'Last Last financial year',
                                            value: LAST_LAST_FINANCIAL_YEAR,
                                            cssClass: 'sebi-radio-button-style',
                                        },
                                    ]}
                                    selectedValue={period}
                                    handleRadioChange={(value: any) => setPeriod(value)}
                                />
                                <div
                                    className="sebi-content-period sebi-detailed-content"
                                    style={{ marginTop: '-20px', marginBottom: '30px' }}
                                >
                                    <h6>From</h6>
                                    <div className="sebi-content-period-left">
                                        {period === SPECIFIC_FINANCIAL_YEAR ? (
                                            <DatePickerInput
                                                value={selectedDate?.from ?? new Date()}
                                                onChange={(date: any) => handleFromDateChange(date)}
                                                minDate={moment()
                                                    .subtract(3, 'years')
                                                    .startOf('year')
                                                    .format('YYYY-MM-DD')}
                                            />
                                        ) : (
                                            <p className="sebi-content-text">
                                                {moment(selectedDate?.from).format('YYYY-MM-DD')}
                                            </p>
                                        )}
                                    </div>
                                    <h6>To</h6>
                                    <div className="sebi-content-period-left">
                                        {period === SPECIFIC_FINANCIAL_YEAR ? (
                                            <DatePickerInput
                                                value={selectedDate?.to ?? new Date()}
                                                onChange={(date: any) => handleToDateChange(date)}
                                                minDate={selectedDate?.from ?? new Date()}
                                            />
                                        ) : (
                                            <p className="sebi-content-text">
                                                {moment(selectedDate?.to).format('YYYY-MM-DD')}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="sebi-content-bottom sebi-content-period-pan">
                            <div className="sebi-content-left" style={{ width: '10%' }}>
                                <p className="sebi-content-left-text">{sebi.pan}</p>
                            </div>
                            <div className="sebi-content-right">
                                <div className="sebi-content-period">
                                    <div className="sebi-content-period-left">
                                        <p className="sebi-content-text">{maskPan(props.pan)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                </>
            ) : (
                <>
                    <div className="sebi-portfolio-downloads">
                        {!!tableRows.length || isCgsHistoryDataLoading ? (
                            <TableWithCollapse columns={tableColumns} rows={tableRows} {...rest} />
                        ) : (
                            <NoFoliosFound info={'No Downloads Found'} />
                        )}
                    </div>
                </>
            )}
            {statementType !== SEGMENTS[1] && (
                <div style={{ display: 'flex', gap: '20px' }}>
                    <div className="sebi-download-btn" style={{ flex: '50%' }}>
                        {isGeneratingDetailedExcel ? (
                            <a className="customBtn">
                                {sebi.submitting}
                                <span className="sending-dots">
                                    <i></i>
                                    <i></i>
                                    <i></i>
                                </span>
                            </a>
                        ) : (
                            <a onClick={() => generateCgsDetail('xlsx')}>{sebi.submit}</a>
                        )}
                    </div>
                    {/* <div className="sebi-download-btn" style={{ flex: '50%' }}> */}
                    {/*     {isGeneratingDetailedPDF ? ( */}
                    {/*         <a className="customBtn"> */}
                    {/*             {sebi.submitting} */}
                    {/*             <span className="sending-dots"> */}
                    {/*                 <i></i> */}
                    {/*                 <i></i> */}
                    {/*                 <i></i> */}
                    {/*             </span> */}
                    {/*         </a> */}
                    {/*     ) : ( */}
                    {/*         <a onClick={() => generateCgsDetail('pdf')}>{sebi.submitPdf}</a> */}
                    {/*     )} */}
                    {/* </div> */}
                </div>
            )}
        </div>
    );
};
export default SebiCgs;
